import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import { restoreFocus } from './useDialog';

export interface ContextMenuItem {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    /** Shown greyed and inert — e.g. the state a row is already in. */
    disabled?: boolean;
}

interface ContextMenuProps {
    x: number;
    y: number;
    items: ContextMenuItem[];
    onClose: () => void;
}

/**
 * A small menu at a point — the right-click menu, or the menu a button opens in place.
 *
 * Keyboard (design audit 2026-10-07, S1-02): it is a `role="menu"`, focus moves to its first
 * enabled item, ↑/↓/Home/End move, Esc closes and focus returns to the element that opened it.
 *
 * **A keyboard-activated click has `clientX/clientY` = 0**, so a caller passing the event's
 * coordinates put the menu at the top-left corner of the screen. When the point is (0, 0) the
 * menu anchors under the element that had focus when it opened — the button that was pressed.
 */
export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const opener = useRef<HTMLElement | null>(
        typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null);
    const [pos, setPos] = useState(() => {
        if (x === 0 && y === 0 && opener.current && opener.current !== document.body) {
            const r = opener.current.getBoundingClientRect();
            return { left: r.left, top: r.bottom + 4 };
        }
        return { left: x, top: y };
    });

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleScroll = () => onClose();

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [onClose]);

    // Focus in on open; back to the opener on close — unless the pick opened something that
    // took focus itself (a modal with autoFocus), which must keep it.
    useEffect(() => {
        menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])')?.focus();
        const returnTo = opener.current;
        return () => restoreFocus(returnTo);
    }, []);

    // On the app's one Esc stack, so Esc inside a dialog closes the menu, not the dialog.
    useTopmostEscape(true, onClose);

    // Keep it within the viewport.
    useLayoutEffect(() => {
        if (!menuRef.current) return;
        const rect = menuRef.current.getBoundingClientRect();
        let { left, top } = pos;
        if (rect.right > window.innerWidth) left = Math.max(4, left - rect.width);
        if (rect.bottom > window.innerHeight) top = Math.max(4, top - rect.height);
        if (left !== pos.left || top !== pos.top) setPos({ left, top });
    }, [pos]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        const enabled = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [])];
        const i = enabled.indexOf(document.activeElement as HTMLButtonElement);
        const move = (to: number) => { e.preventDefault(); enabled[(to + enabled.length) % enabled.length]?.focus(); };
        if (e.key === 'ArrowDown') move(i + 1);
        else if (e.key === 'ArrowUp') move(i - 1);
        else if (e.key === 'Home') move(0);
        else if (e.key === 'End') move(enabled.length - 1);
        else if (e.key === 'Tab') { e.preventDefault(); onClose(); }
    };

    if (items.length === 0) return null;

    return (
        <div
            ref={menuRef}
            role="menu"
            onKeyDown={onKeyDown}
            className="fixed z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            style={{ left: pos.left, top: pos.top }}
        >
            {items.map((item, i) => (
                <button
                    key={i}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={() => {
                        item.onClick();
                        onClose();
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm focus:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                        item.disabled
                            ? 'cursor-default text-gray-500'
                            : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                    {item.icon && <span className="text-gray-500" aria-hidden="true">{item.icon}</span>}
                    {item.label}
                </button>
            ))}
        </div>
    );
}
